import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const LAST_UPDATE = "28 febbraio 2026";

const IT = {
  title: "Cookie Policy",
  intro: `Questo sito utilizza esclusivamente cookie e tecnologie di archiviazione locale strettamente necessari al suo funzionamento, in conformità al Provvedimento del Garante Privacy n. 231/2021 e all'art. 122 del Codice Privacy (D.Lgs. 196/2003).`,
  sections: [
    {
      h: "1. Cosa sono i cookie",
      p: [
        `I cookie sono piccoli file di testo che i siti web salvano sul dispositivo dell'utente. Insieme ai cookie, alcuni siti utilizzano tecnologie simili (localStorage, sessionStorage) che svolgono funzioni analoghe. In questa informativa il termine "cookie" comprende entrambe le tipologie.`,
      ],
    },
    {
      h: "2. Cookie tecnici utilizzati da questo sito",
      p: [
        `<strong>Preferenza lingua</strong> (localStorage: <code>eterna_lang</code>) — memorizza la scelta della lingua (IT/EN) per fornire il sito nella lingua corretta ad ogni visita. Durata: persistente fino a cancellazione manuale.`,
        `<strong>Sessione admin</strong> (localStorage: <code>eterna_admin_token</code>) — utilizzato SOLO nell'area riservata /admin, per mantenere la sessione autenticata del titolare. Nessun impatto sull'utente pubblico. Durata: 7 giorni.`,
        `<strong>Preview toggle</strong> (localStorage: <code>eterna_preview</code>) — flag opzionale per anteprima interna. Non utilizzato in produzione. Durata: fino a cancellazione manuale.`,
        `Questi cookie sono <strong>strettamente necessari</strong> e non richiedono consenso preventivo ai sensi dell'art. 122 del Codice Privacy.`,
      ],
    },
    {
      h: "3. Cookie NON utilizzati",
      p: [
        `Il sito NON utilizza:`,
        `— cookie di profilazione o marketing;`,
        `— cookie di terze parti per pubblicità comportamentale;`,
        `— pixel di tracciamento social (Facebook Pixel, LinkedIn Insight, TikTok Pixel);`,
        `— strumenti di analytics con cookie (Google Analytics, Adobe Analytics);`,
        `— strumenti di remarketing.`,
        `Per questo motivo non è presente alcun banner cookie: nessun trattamento richiede un consenso preventivo.`,
      ],
    },
    {
      h: "4. Servizi di terze parti embedded",
      p: [
        `Il sito potrebbe caricare risorse esterne (font Google, immagini CDN Cloudflare, script tecnici). Queste risorse non installano cookie sul dispositivo dell'utente, ma il tuo indirizzo IP viene inviato al fornitore per il caricamento tecnico. I fornitori coinvolti sono:`,
        `— <strong>Cloudflare, Inc.</strong> — CDN, sicurezza e DNS.`,
        `— <strong>Google Fonts</strong> — caricamento tipografico (nessun cookie).`,
        `Consulta la <a href="/privacy" style="color:#C4A462;text-decoration:underline">Privacy Policy</a> per ulteriori dettagli sui trattamenti.`,
      ],
    },
    {
      h: "5. Come disabilitare i cookie",
      p: [
        `Puoi disabilitare i cookie tecnici direttamente dal tuo browser. Attenzione: disabilitandoli, alcune funzioni del sito (come la preferenza di lingua) potrebbero non funzionare correttamente.`,
        `— <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" style="color:#C4A462;text-decoration:underline">Chrome</a>`,
        `— <a href="https://support.mozilla.org/it/kb/cookie-informazioni-siti-web-memorizzano-computer" target="_blank" rel="noopener noreferrer" style="color:#C4A462;text-decoration:underline">Firefox</a>`,
        `— <a href="https://support.apple.com/it-it/HT201265" target="_blank" rel="noopener noreferrer" style="color:#C4A462;text-decoration:underline">Safari</a>`,
        `— <a href="https://support.microsoft.com/it-it/windows/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" style="color:#C4A462;text-decoration:underline">Edge</a>`,
      ],
    },
    {
      h: "6. Aggiornamenti",
      p: [
        `Questa Cookie Policy può essere aggiornata in caso di introduzione di nuovi strumenti o modifiche normative. In caso di introduzione di cookie di profilazione o di terze parti richiedenti consenso, verrà mostrato un banner cookie conforme.`,
      ],
    },
  ],
};

const EN = {
  title: "Cookie Policy",
  intro: `This website uses exclusively cookies and local storage technologies strictly necessary for its functioning, in compliance with the Italian Data Protection Authority Provision no. 231/2021 and art. 122 of the Italian Privacy Code (Legislative Decree 196/2003).`,
  sections: [
    {
      h: "1. What are cookies",
      p: [
        `Cookies are small text files that websites save on the user's device. Alongside cookies, some sites use similar technologies (localStorage, sessionStorage) with equivalent purposes. In this notice the term "cookie" covers both.`,
      ],
    },
    {
      h: "2. Technical cookies used by this website",
      p: [
        `<strong>Language preference</strong> (localStorage: <code>eterna_lang</code>) — stores the language choice (IT/EN). Duration: persistent until manually cleared.`,
        `<strong>Admin session</strong> (localStorage: <code>eterna_admin_token</code>) — used ONLY in the reserved /admin area, to maintain the owner's authenticated session. No impact on public users. Duration: 7 days.`,
        `<strong>Preview toggle</strong> (localStorage: <code>eterna_preview</code>) — optional internal preview flag. Not used in production. Duration: until manually cleared.`,
        `These cookies are <strong>strictly necessary</strong> and require no prior consent under art. 122 of the Italian Privacy Code.`,
      ],
    },
    {
      h: "3. Cookies NOT used",
      p: [
        `The site does NOT use:`,
        `— profiling or marketing cookies;`,
        `— third-party behavioural advertising cookies;`,
        `— social tracking pixels (Facebook Pixel, LinkedIn Insight, TikTok Pixel);`,
        `— cookie-based analytics tools (Google Analytics, Adobe Analytics);`,
        `— remarketing tools.`,
        `For this reason, no cookie banner is shown: no processing requires prior consent.`,
      ],
    },
    {
      h: "4. Embedded third-party services",
      p: [
        `The site may load external resources (Google fonts, Cloudflare CDN images, technical scripts). These resources do not install cookies on the user's device, but the user's IP is sent to the provider for technical loading. Providers involved:`,
        `— <strong>Cloudflare, Inc.</strong> — CDN, security and DNS.`,
        `— <strong>Google Fonts</strong> — typography loading (no cookies).`,
        `See the <a href="/privacy" style="color:#C4A462;text-decoration:underline">Privacy Policy</a> for further processing details.`,
      ],
    },
    {
      h: "5. How to disable cookies",
      p: [
        `You can disable technical cookies directly from your browser. Warning: disabling them may cause some features (like the language preference) to malfunction.`,
        `— <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" style="color:#C4A462;text-decoration:underline">Chrome</a>`,
        `— <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" style="color:#C4A462;text-decoration:underline">Firefox</a>`,
        `— <a href="https://support.apple.com/en-us/HT201265" target="_blank" rel="noopener noreferrer" style="color:#C4A462;text-decoration:underline">Safari</a>`,
        `— <a href="https://support.microsoft.com/en-us/windows/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" style="color:#C4A462;text-decoration:underline">Edge</a>`,
      ],
    },
    {
      h: "6. Updates",
      p: [
        `This Cookie Policy may be updated when new tools are introduced or regulations change. Should profiling or third-party consent-requiring cookies be introduced, a compliant cookie banner will be displayed.`,
      ],
    },
  ],
};

export default function CookiePage() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "it";
  const C = lang === "en" ? EN : IT;

  return (
    <div data-testid="cookie-page" className="min-h-screen bg-[#FAF9F6]" style={{ color: "#1A1B1A" }}>
      <Helmet>
        <html lang={lang} />
        <title>{`${C.title} — ETERNA Motion`}</title>
        <meta name="description" content={C.intro.substring(0, 160)} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://eternamotion.com/cookie" />
      </Helmet>

      <section className="pt-32 lg:pt-40 pb-16 px-6 lg:px-12 max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C4A462] mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
            {lang === "en" ? "Legal" : "Note legali"}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
            {C.title}
          </h1>
          <div className="w-16 h-px bg-[#C4A462] mb-8" />
          <p className="text-base text-[#4A4A4A] leading-relaxed mb-12" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
            {C.intro}
          </p>

          {C.sections.map((s) => (
            <div key={s.h} className="mb-10">
              <h2 className="text-xl sm:text-2xl tracking-tight text-[#1A1B1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {s.h}
              </h2>
              {s.p.map((para, i) => (
                <p
                  key={i}
                  className="text-sm text-[#4A4A4A] leading-relaxed mb-3"
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
                  dangerouslySetInnerHTML={{ __html: para }}
                />
              ))}
            </div>
          ))}

          <p className="text-xs uppercase tracking-[0.2em] text-[#757575] mt-6 pt-8 border-t border-[#D8D4CC]" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
            {lang === "en" ? "Last updated" : "Ultimo aggiornamento"}: {LAST_UPDATE}
          </p>

          <Link to="/" className="inline-block mt-12 text-[10px] uppercase tracking-[0.25em] text-[#C4A462] hover:text-[#1A1B1A] transition-colors" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }} data-testid="cookie-back-home">
            ← {lang === "en" ? "Back to home" : "Torna alla home"}
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
